import {
  IconButton,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { UsageChartIcon } from '@vy/ui-design-system';
import type { GetCustomerContractsOverviewPageResponse } from '@vy/api-definition';
import { buildCurrencyLevel, formatPrice, isUsageProduct } from '@vy/services';
import type { Currency } from '@vy/types';
import { CONTRACT_STATUSES } from '@vy/types';
import { isNotNullOrUndefined, vyDate } from '@vy/utils';
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { VyPaper } from '../../../../../../../components/new-design/data-display/VyPaper/VyPaper';
import { calculateEstimatedPlanValue } from '../../../../../../../utils/contract/calculateEstimatedPlanValue';
import { ContractUsageDialog } from './ContractUsageDialog/ContractUsageDialog';

type ContractDialogState = {
  contractId: string;
  contractName: string;
  customerId: string;
  products: GetCustomerContractsOverviewPageResponse[number]['contract']['plan']['products'];
  currency: Currency;
};

type CustomerActiveContractsProps = {
  contracts: GetCustomerContractsOverviewPageResponse;
  isLoading: boolean;
};

export const CustomerActiveContracts: React.FC<CustomerActiveContractsProps> = ({ contracts, isLoading }) => {
  const activeContracts = getActiveContracts(contracts);
  const [dialogState, setDialogState] = useState<ContractDialogState | null>(null);

  const handleViewUsageOpen = (
    contractId: string,
    contractName: string,
    customerId: string,
    products: GetCustomerContractsOverviewPageResponse[number]['contract']['plan']['products'],
    currency: Currency,
  ) => {
    setDialogState({ contractId, contractName, customerId, products, currency });
  };

  const handleViewUsageClose = () => {
    setDialogState(null);
  };

  return (
    <VyPaper>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 500,
          marginBottom: "20px"
        }}>
        Active Contracts ({activeContracts.length})
      </Typography>
      {isLoading ? (
        <ActiveContractsTableSkeleton />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'text.primary', fontSize: 12 }}>
                Contract Name
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.primary', fontSize: 12 }}>Period</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary', fontSize: 12 }}>
                Value Estimation
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: 'text.primary', fontSize: 12 }}>
                Usage Data
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeContracts.map(({ contract }) => {
              const currency = buildCurrencyLevel({
                contractCurrency: contract.currencyOverride,
                customerCurrency: contract.customer.settings?.currency,
              });

              const hasUsageProducts = contract.plan.products.some(isUsageProduct);

              return (
                <TableRow key={contract.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell sx={{ fontSize: 14 }}>
                    <Link
                      component={NavLink}
                      to={`/customers/${contract.customer.id}/contracts/${contract.id}`}
                      underline="hover"
                    >
                      {contract.plan.name}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ fontSize: 14 }}>{formatContractPeriod(contract)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 14 }}>
                    {formatPrice(calculateEstimatedPlanValue(contract.plan), currency)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                    {hasUsageProducts && (
                      <IconButton
                        data-vy="customer-overview-active-contracts-view-usage-button"
                        size="small"
                        onClick={() =>
                          handleViewUsageOpen(
                            contract.id,
                            contract.plan.name,
                            contract.customer.id,
                            contract.plan.products,
                            currency,
                          )
                        }
                      >
                        <UsageChartIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {isNotNullOrUndefined(dialogState) && (
        <ContractUsageDialog
          key={dialogState.contractId}
          open
          onClose={handleViewUsageClose}
          contractId={dialogState.contractId}
          contractName={dialogState.contractName}
          customerId={dialogState.customerId}
          products={dialogState.products}
          currency={dialogState.currency}
        />
      )}
    </VyPaper>
  );
};

function ActiveContractsTableSkeleton() {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>
            <Skeleton variant="text" width={120} height={20} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={160} height={20} />
          </TableCell>
          <TableCell align="right">
            <Skeleton variant="text" width={80} height={20} sx={{ marginLeft: 'auto' }} />
          </TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {[0, 1, 2].map((index) => (
          <TableRow key={index}>
            <TableCell>
              <Skeleton variant="text" width={140} height={20} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width={180} height={20} />
            </TableCell>
            <TableCell align="right">
              <Skeleton variant="text" width={80} height={20} sx={{ marginLeft: 'auto' }} />
            </TableCell>
            <TableCell />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function getActiveContracts(contracts: GetCustomerContractsOverviewPageResponse) {
  return contracts.filter(({ contract }) => contract.status === CONTRACT_STATUSES.Active);
}

function formatContractPeriod(
  contract: GetCustomerContractsOverviewPageResponse[number]['contract'],
): string {
  const start = vyDate(contract.startDate).utc().format('DD MMM. YYYY');
  const end = isNotNullOrUndefined(contract.endDate)
    ? vyDate(contract.endDate).utc().format('DD MMM. YYYY')
    : 'Ongoing';

  return `${start} — ${end}`;
}
